/**
 * Plan G8 — post-signup marketing-setup onboarding nudge.
 *
 * Surfaces the "Your app is built. Connect channels to launch marketing →"
 * banner once the user has completed at least one build but not yet
 * connected any channels (Resend / Google Ads / Meta).
 *
 * The hook does two things:
 *   1. Reads /api/onboarding/marketing-nudge/state via TanStack Query
 *      (server state, cached by the global QueryClient).
 *   2. Exposes a `dismiss()` mutation that POSTs to the dismiss endpoint
 *      and invalidates the cache so the banner disappears immediately.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiFetch, ApiError, getAuthToken } from '@/lib/api'

export interface MarketingNudgeState {
  show_marketing_nudge: boolean
  total_builds: number
  connectors_connected: number
  dismissed: boolean
}

const QUERY_KEY = ['onboarding', 'marketing-nudge', 'state']

export function useMarketingNudge() {
  const queryClient = useQueryClient()
  // Avoid firing when the user is unauthenticated — the endpoint 401s,
  // we shouldn't pollute the network panel for logged-out visitors.
  const enabled = !!getAuthToken()

  const query = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () =>
      apiFetch<MarketingNudgeState>('/api/onboarding/marketing-nudge/state'),
    enabled,
    // Banner state is low-frequency — 60s freshness keeps render counts
    // honest while still flipping false→true ~1min after the first build.
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    retry: (failureCount, error) => {
      // Don't retry on 401 (logged out) or 404 (route missing). The
      // banner is non-critical UX; failing silently is correct.
      if (error instanceof ApiError && (error.status === 401 || error.status === 404)) {
        return false
      }
      return failureCount < 2
    },
  })

  const dismissMutation = useMutation({
    mutationFn: () =>
      apiFetch<{ dismissed: boolean }>(
        '/api/onboarding/marketing-nudge/dismiss',
        { method: 'POST' },
      ),
    onSuccess: () => {
      // Optimistic: flip the cache so the banner disappears synchronously
      // even before the GET re-fetches.
      queryClient.setQueryData<MarketingNudgeState | undefined>(
        QUERY_KEY,
        (prev) =>
          prev
            ? { ...prev, show_marketing_nudge: false, dismissed: true }
            : prev,
      )
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY })
    },
  })

  return {
    state: query.data,
    isLoading: query.isLoading,
    error: query.error,
    show: !!query.data?.show_marketing_nudge,
    dismiss: () => dismissMutation.mutate(),
    isDismissing: dismissMutation.isPending,
  }
}
